import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { setBasicDialogOpenFor, setIsLoadingStatus, setOpenToast } from "../../../store/helpers/helperReducers";
import { useStoreDispatch } from "../../../store/hooks";
import { useApplyTemplateMutation, useImportTemplateMutation } from "../../../store/api/tasks/taskApiSlice";
import { resetSelectedTemplate } from "../../helpers";
import { selectedGlobalTemplate, selectedTemplate } from "../../../store/api/devices/reducers";
import { errorMessage } from "../../../utils/helpers";
import { selectedTypeSelector } from "../../../store/api/devices/selectors";

export const useTemplateDisplay = () => {
  const dispatch = useStoreDispatch();
  const [ApplyTemplate, applyStatus] = useApplyTemplateMutation();
  const [importTemplate, inportStatus] = useImportTemplateMutation();
  const selectedTempType = useSelector(selectedTypeSelector);

  const location = useLocation();
  const pathSegments = location?.pathname?.split("/").filter(Boolean) || [];
  const deviceIndex = pathSegments.indexOf("device");
  const deviceKey = deviceIndex !== -1 && deviceIndex + 1 < pathSegments.length
    ? decodeURIComponent(pathSegments[deviceIndex + 1])
    : "NoDevice";
  useEffect(() => {
    dispatch(setIsLoadingStatus({ importFile: inportStatus.isLoading }));
  }, [dispatch, inportStatus.isLoading]);

  useEffect(() => {
    dispatch(setIsLoadingStatus({ apply: applyStatus.isLoading }));
  }, [dispatch, applyStatus.isLoading]);

  const handleImportTempate = async (
    file: File,
    closeToDialogOpenFor?: string,
    description: string = ""
  ) => {
    if (file) {
      const extensionFromName = file?.name?.split(".").pop()?.toLowerCase();
      let fileType = extensionFromName === "xml" || extensionFromName === "csv" ? extensionFromName : "";
      if (!fileType) {
        const mimeSubtype = file?.type?.split("/")[1]?.toLowerCase();
        fileType = mimeSubtype === "vnd.ms-excel" ? "csv" : mimeSubtype || "";
      }
      if (fileType !== "xml" && fileType !== "csv") {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: "Invalid file type. Supported formats are XML or CSV." }));
        return;
      }
      const formData = new FormData();
      formData.append("file", file);
      try {
        const result: any = await importTemplate({ formData, fileType, deviceKey, description });
        if (result?.error || result?.data?.status === 401) throw result?.error;
        if (result?.data?.status === 200 || result?.statusText === "OK") {
          // refetch();
          dispatch(setBasicDialogOpenFor(closeToDialogOpenFor));
          dispatch(setIsLoadingStatus({ importFile: false }));
          dispatch(
            setOpenToast({
              openToast: true,
              toastType: "success",
              message: "Template import has initiated!",
            })
          );
        }
      } catch (error: any) {
        dispatch(setBasicDialogOpenFor(closeToDialogOpenFor));
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: error?.data ? JSON.stringify(error?.data) : errorMessage(error) }));
      }
    }
  };

  const handleApplyTemplate = async (selectedRow: any, type: string) => {
    let templateName = selectedRow?.templateName;
    if (templateName) {
      const encodedUri = encodeURIComponent(templateName || "") || "";
      const decodedHtmlEntitiesPayload = encodedUri.replace(/%26amp%3B/g, '%26');
      try {
        const templateArgs = {
          fileType: "xml",
          deviceKey: deviceKey !== "NoDevice" ? deviceKey : "",
          templateName: decodedHtmlEntitiesPayload || "",
        };
        const result: any = await ApplyTemplate(templateArgs);
        if (result?.error || result?.status === 401) throw result?.error;
        if (result?.data?.status === 200 || result?.statusText === "OK") {
          dispatch(setOpenToast({ openToast: true, toastType: "success", message: "Template apply has initiated!" }));
          if (selectedTempType === "global") {
            dispatch(selectedGlobalTemplate(selectedRow));
          } else if (selectedTempType === "local") {
            dispatch(selectedTemplate(selectedRow));
          }
        }
      } catch (error: any) {
        dispatch(setOpenToast({ openToast: true, toastType: "error", message: error?.data ? JSON.stringify(error?.data) : errorMessage(error) }));
        if (type === "global") dispatch(selectedGlobalTemplate(resetSelectedTemplate));
        else if (type === "local") dispatch(selectedTemplate(resetSelectedTemplate));
      }
    }
  };

  return {
    handleImportTempate,
    handleApplyTemplate,
  };
};
